<template>
  <div class="tabs-container" :class="{ vertical: layout === TABS_LAYOUT.VERTICAL }">
    <div class="tabs-wrapper">
      <i
        v-if="showArrows && layout !== TABS_LAYOUT.VERTICAL"
        class="fa-solid fa-chevron-left flechas pointer"
        @click="handleScrollLeft">
      </i>

      <div
        ref="scrollContainer"
        class="tabs-scroll-container"
        @mousedown="startDragging"
        @mousemove="onDrag"
        @mouseup="stopDragging"
        @mouseleave="stopDragging"
        @touchstart="startDragging"
        @touchmove="onDrag"
        @touchend="stopDragging">
        <div class="tabs-content">
          <div v-for="tab in tabs" :key="tab.id" class="tab-item">
            <button class="tab-button" :class="{ active: activeTab === tab.id }" @click="handleTabClick(tab)">
              {{ tab.label }}
            </button>
            <div class="tab-indicator" :class="{ active: activeTab === tab.id }"></div>
          </div>
        </div>
      </div>
      <i
        v-if="showArrows && layout !== TABS_LAYOUT.VERTICAL"
        class="fa-solid fa-chevron-right flechas pointer"
        @click="handleScrollRight">
      </i>
    </div>
  </div>
</template>

<script setup>
  import { useDraggableScroll, useScrollableContainer } from '@/helpers/gestureUtils';
  import { TABS_LAYOUT, TABS_MODE } from '@/helpers/tabsUtils';

  import { ref, onMounted, onBeforeUnmount } from 'vue';

  const props = defineProps({
    tabs: {
      type: Array,
      required: true,
    },
    initialTab: {
      type: String,
      default: null,
    },
    mode: {
      type: String,

      default: TABS_MODE.SCROLL,
      validator: value => Object.values(TABS_MODE).includes(value),
    },
    layout: {
      type: String,
      default: TABS_LAYOUT.HORIZONTAL,
      validator: value => Object.values(TABS_LAYOUT).includes(value),
    },
    scrollOffset: {
      type: Number,
      default: 180,
    },
  });

  const emit = defineEmits(['tab-changed']);

  const { scrollContainer, showArrows, handleScrollLeft, handleScrollRight, setupScrollableContainer } =
    useScrollableContainer();
  const { startDragging, onDrag, stopDragging } = useDraggableScroll();

  const activeTab = ref(props.initialTab || props.tabs[0]?.id);

  function scrollToSection(tabId) {
    activeTab.value = tabId;
    emit('tab-changed', tabId);
    const element = document.getElementById(`section-${tabId}`);
    if (element) {
      window.scrollTo({
        top: element.getBoundingClientRect().top + window.pageYOffset - props.scrollOffset,
        behavior: 'smooth',
      });
    }
  }

  function handlePageScroll() {
    if (props.mode !== TABS_MODE.SCROLL) return;

    const offset = props.scrollOffset;
    let closest = null;
    let minDist = Infinity;

    props.tabs.forEach(tab => {
      const element = document.getElementById(`section-${tab.id}`);
      if (element) {
        const top = element.getBoundingClientRect().top;
        const dist = Math.abs(top - offset);
        if (top <= offset + 50 && dist < minDist) {
          minDist = dist;
          closest = tab.id;
        }
      }
    });

    const newActive = closest || props.tabs[0]?.id;
    if (newActive !== activeTab.value) {
      activeTab.value = newActive;
      emit('tab-changed', newActive);
    }
  }

  function handleTabClick(tab) {
    if (props.mode === TABS_MODE.SCROLL) {
      scrollToSection(tab.id);
      return;
    }
    activeTab.value = tab.id;
    emit('tab-changed', tab.id);
  }

  onMounted(() => {
    if (props.mode === TABS_MODE.SCROLL) {
      window.addEventListener('scroll', handlePageScroll);
      handlePageScroll();
    }

    if (props.layout !== TABS_LAYOUT.VERTICAL) {
      setupScrollableContainer({
        source: props.tabs,
        enabled: true,
        debounceTime: 100,
      });
    }
  });

  onBeforeUnmount(() => {
    if (props.mode === TABS_MODE.SCROLL) {
      window.removeEventListener('scroll', handlePageScroll);
    }
  });
</script>

<style lang="scss" scoped>
  .tabs-container {
    width: 100%;
    position: sticky;
    top: 0;
    background: white;
    z-index: 10;
  }

  .tabs-container.vertical {
    position: static;
    z-index: auto;

    .tabs-wrapper {
      border-bottom: none;
    }

    .tabs-scroll-container {
      overflow: visible;
    }

    .tabs-content {
      flex-direction: column;
      align-items: stretch;
    }

    .tab-item {
      align-items: stretch;
      width: 100%;
      flex: none;
      min-width: 0;
    }

    .tab-button {
      text-align: left;
      min-height: auto;
      max-height: none;
    }

    .tab-indicator {
      display: block;
      bottom: auto;
      top: 0;
      left: 0;
      width: 1px;
      height: 100%;
      background-color: rgba(var(--bs-primary-rgb), 0.2);
    }

    .tab-indicator.active {
      width: 2px;
      background-color: var(--bs-primary);
    }
  }

  .tabs-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    border-bottom: 1px solid var(--bs-gray-300);
  }

  .tabs-scroll-container {
    width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .tabs-scroll-container::-webkit-scrollbar {
    display: none;
  }

  .tabs-content {
    display: flex;
    position: relative;
    width: 100%;
  }

  .tab-item {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  .tab-item.active {
    color: var(--bs-primary);
    font-weight: 600;
    background-color: #f9f7f8;
    height: 100%;
  }

  @media screen and (min-width: 768px) {
    .tab-item {
      flex: 1;
    }
  }

  @media screen and (max-width: 768px) {
    .tab-item {
      min-width: max-content;
    }
  }

  .tab-button {
    padding: 10px 20px;
    background: none;
    border: none;
    color: var(--bs-primary);
    font-size: 0.9rem;
    font-weight: 500;
    transition: all 0.3s ease;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
    width: 100%;
  }

  .tab-button:hover {
    color: var(--bs-primary);
  }

  .tab-button.active {
    color: var(--bs-primary);
    font-weight: 600;
    background-color: #f9f7f8;
    height: 100%;
  }

  .tab-indicator {
    position: absolute;
    bottom: -1px;
    height: 2px;
    background-color: var(--bs-primary);
    width: 100%;
    display: none;
  }

  .tab-indicator.active {
    display: block;
  }

  .flechas {
    position: absolute;
    color: var(--bs-primary);
    font-size: 15px;
    cursor: pointer;
    z-index: 1;
    height: 100%;
    display: flex;
    align-items: center;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0) 100%);
    display: none;
  }

  @media screen and (max-width: 767px) {
    .flechas {
      display: flex;
    }
  }

  .flechas:first-child {
    left: 0;
  }

  .flechas:last-child {
    right: 0;
    background: linear-gradient(270deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0) 100%);
  }
</style>
